import sanitize from './sanitize';
import { isSuccessful, xhrPostWithSession } from './fetch';
import { URLS } from './url';
import { round } from '../services/prettify';
import {
  outboxMessageProcessed,
  outboxSendMessage as sendMessageAction,
  outboxUpdateProgress,
  draftUpdateProgress,
  draftClean,
  outboxSetSent,
  draftSetSent,
  outboxSetError,
  draftSetError,
  editMessage
} from '../actions/application';
import ACTIONS from '../actions/lexon';
import { documentIdClear } from '../actions/user';
import { resetSyncsImap } from './sync-imap';
import moment from 'moment';

const SNACKBAR_DURATION = 800;

function getNewTokenLexon(token, dispatch) {
  dispatch(ACTIONS.setLexonToken(token.replace('/', '')));
}

export function sendMessage(
  dispatch,
  credentials,
  lexon,
  {
    inReplyTo = [],
    references = [],
    to,
    cc,
    bcc,
    attachments = [],
    subject,
    content,
    important,
    readConfirmation,
    receiptConfirmation
  }
) {
  const normalizedAttachments = (attachments) ? attachments.map(at => ({ ...at, fileName: removeAccents(at.fileName)})) : [];
  const message = {
    recipients: [
      ...to.map(address => ({ type: 'To', address: address })),
      ...cc.map(address => ({ type: 'Cc', address: address })),
      ...bcc.map(address => ({ type: 'Bcc', address: address }))
    ],
    inReplyTo,
    references,
    attachments: normalizedAttachments,
    subject: subject,
    content: sanitize.sanitize(content),
    important: important,
    readConfirmation: readConfirmation,
    receiptConfirmation: receiptConfirmation
  };
  const errorHandler = () => {
    dispatch(outboxSetError(true));
  };
  dispatch(sendMessageAction(message));
  // PEC-8747: un 401 renueva la sesión y repite el envío una vez; cualquier rechazo definitivo (incluido
  // "Username and Password not accepted" de SMTP) deja el mensaje en el editor con el error del outbox.
  xhrPostWithSession(URLS.SMTP, JSON.stringify(message), {
    credentials,
    onProgress: e => dispatch(outboxUpdateProgress(round(e.loaded / e.total, 2))),
    onError: errorHandler,
    onLoad: event => {
    if (isSuccessful(event.target.status)) {
      if (event && event.target && event.target.response) {
        dispatch(outboxSetSent(true, event.target.response, false));
      } else {
        dispatch(outboxSetSent(true, '', false));
      }
      resetSyncsImap({...lexon, user: lexon.account});
      dispatch(draftClean());
      setTimeout(() => dispatch(outboxMessageProcessed()), SNACKBAR_DURATION);
      dispatch(ACTIONS.setDocumentsId(null));
      dispatch(ACTIONS.setInterveners(null));
      dispatch(editMessage(null));
      dispatch(ACTIONS.setCaseFile({
        casefile: null,
        bbdd: lexon.bbdd,
        idCompany: lexon.idCompany
      }));
      dispatch(documentIdClear(null));
      window.dispatchEvent(new CustomEvent("ResetList"));
      window.dispatchEvent(new CustomEvent("RemoveCaseFile"));
      if (lexon.interveners?.length > 0 || lexon.idCaseFile || lexon.documentsId) {
        getNewTokenLexon(lexon.token, dispatch);
      }
    } else {
      errorHandler();
    }
    }
  });
}

export function saveDraft(
  dispatch,
  credentials,
  {
    inReplyTo = [],
    references = [],
    to,
    cc,
    bcc,
    attachments = [],
    subject,
    content,
    important,
    readConfirmation,
    receiptConfirmation
  }
) {
  const normalizedAttachments = (attachments) ? attachments.map(at => ({ ...at, fileName: removeAccents(at.fileName)})) : [];
  const message = {
    recipients: [
      ...to.map(address => ({ type: 'To', address: address })),
      ...cc.map(address => ({ type: 'Cc', address: address })),
      ...bcc.map(address => ({ type: 'Bcc', address: address }))
    ],
    inReplyTo,
    references,
    attachments: normalizedAttachments,
    subject: subject,
    content: sanitize.sanitize(content),
    important: important,
    readConfirmation: readConfirmation,
    receiptConfirmation: receiptConfirmation
  };
  const errorHandler = () => {
    dispatch(draftSetError(true));
  };
  // PEC-8747: mismo tratamiento de sesión que el envío (401 -> renovar -> repetir una vez)
  xhrPostWithSession(URLS.DRAFT, JSON.stringify(message), {
    credentials,
    onProgress: e => dispatch(draftUpdateProgress(round(e.loaded / e.total, 2))),
    onError: errorHandler,
    onLoad: event => {
      if (isSuccessful(event.target.status)) {
        if (event && event.target && event.target.response) {
          dispatch(draftSetSent(true, event.target.response, false));
        } else {
          dispatch(draftSetSent(true, '', false));
        }
      } else {
        errorHandler();
      }
    }
  });
}

const removeAccents = str => str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "");


// if customAttendees is empty then the notification is sent to all the attendees that are included in event object.
// if customAttendees has a value then the notification is sent to the customAttendees only
export function sendNotificationAttendees(lexonToken, idNavision, event, type, customAttendees = [], payload = '') {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append("Authorization", `Bearer ${lexonToken}`);
  const attendeesObj = [];
  let attendeesToNotifyObj = [];
  event.attendees.forEach(attendee => {
    attendeesObj.push({name: attendee.name, email: attendee.email});
  });

  if (customAttendees.length === 0) {
    attendeesToNotifyObj = attendeesObj;
  } else if (customAttendees.length > 0) {
    customAttendees.forEach(attendee => {
      attendeesToNotifyObj.push({name: attendee, email: attendee});
    });
  }

  const rawBody = {
    idClienteNav: idNavision,
    type: type,
    organizer: {
      name: event.organizer.name,
      email: event.organizer.email
    },
    attendees: attendeesObj,
    attendeesToNotify: attendeesToNotifyObj,
    info: {
      title: event.summary,
      description: event.description,
      startDate: event.allDay ? `${moment(event.start).add(-1, 'days').format('YYYY-MM-DD')} (Todo el día)` : `${event.start}`,
      endDate: event.allDay ? `${moment(event.end).add(-1, 'days').format('YYYY-MM-DD')}` : `${event.end}`,
      location: event.location
    },
    ics: payload
  };

  const raw = JSON.stringify(rawBody);

  const requestOptions = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow'
  };

  fetch(`${window.PATH_GW_ACC}/${window.API_V2}/nxauth/notification/eventInvitation`, requestOptions)
    .then(response => response.text())
    .then(() => {})
    .catch(error => console.error('error', error));
}

export function sendCalendarLink(lexonToken, idNavision, calendarInfo, receiversInfo, ownerInfo, sign) {
  return new Promise((resolve, reject) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${lexonToken}`);

    const receiversObj = [];
    receiversInfo.forEach(receiver => {
      receiversObj.push({name: "", email: receiver.text});
    });

    const rawBody = {
      idClienteNav: idNavision,
      calendarName: calendarInfo.name,
      calendarDescription: calendarInfo.description,
      downloadLink: calendarInfo.downloadLink,
      receivers: receiversObj,
      owner: ownerInfo,
      sign: sign
    };

    const raw = JSON.stringify(rawBody);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    fetch(`${window.PATH_GW_ACC}/${window.API_V2}/nxauth/notification/shareCalendarLink`, requestOptions)
      .then(response => response.text())
      .then(result => {
        resolve(result);
      })
      .catch(error => {
        console.error('error', error);
        reject(error);
      });
  });
}
